import {Form} from '../ui/form';
import {TextInput} from '../ui/textinput';
import {ButtonRole, StandardButton} from '../constants';
import {apiService as svc} from '../services';
import {closestMatchingElement, isNumber, stringRepeat} from '../util';
import {Dialog} from '../ui/dialog';
import {Button} from '../ui/button';
import {VBoxLayout} from '../ui/layout';
import {El} from '../el';
import {ComboBox} from '../ui/combobox';

class ParentSelect extends ComboBox {
	parentID(): number | null {
		const rv = Number.parseInt(this.value());
		return isNumber(rv) ? rv : null;
	}

	setParentID(value: number | string | null): void {
		this.setValue((value || isNumber(value)) ? String(value) : '');
	}
}

class ObjectForm extends Form {
	nameInput: TextInput;
	parentSelect: ParentSelect;

	constructor() {
		super();
		const box = new VBoxLayout(this);
		box.setSpacing(16);
		this.nameInput = new TextInput({
			inputId: 'id_price-group-name-label',
			name: 'name',
			labelText: 'Name',
			required: true,
		});
		box.addEl(this.nameInput);
		const parentSelectId = 'id_parent-select';
		this.parentSelect = new ParentSelect({
			emptyItem: true,
			labelText: 'Parent group',
		});
		this.parentSelect.setFixPosition(true);
		this.parentSelect.setName('parentId');
		this.parentSelect.setId(parentSelectId);
		box.addEl(this.parentSelect);
		this.setMethod('POST');
	}

	setParents(objs: IPriceGroup[]): void {
		const ctrl = this.parentSelect;
		ctrl.clearItems();
		for (let i = 0; i < objs.length; ++i) {
			const obj = objs[i];
			ctrl.addItem({value: obj.id, listItemOptions: {text: `${stringRepeat('-', obj.level)} ${obj.name}`}});
		}
	}
}

class DocumentObject {
	_objectID: number;
	_primaryText: El;
	_root: El | null;

	constructor(objectID: number) {
		this._objectID = objectID;
		this._primaryText = El.fromSelector(`[data-id="${this._objectID}"]`);
		this._root = this._primaryText.closestMatchingAncestor('.pb-price-group-list-item:not(.pb-price-group-list-item--perma)');
	}

	remove(): void {
		this._root && this._root.remove();
	}

	setName(value: string): void {
		this._primaryText.setText(value);
	}
}

class Dia extends Dialog {
	theForm: Form;

	constructor(theForm: Form) {
		super();
		this.theForm = theForm;
		this.setContent(this.theForm);
	}

	protected buttonClicked(button: Button): void {
		if (this.buttonRole(button) === ButtonRole.AcceptRole && !this.theForm.isValid()) {
			return;
		}
		super.buttonClicked(button);
	}
}

class View {
	static CREATE_CTA_SELECTOR = '#id_object-create-button';
	static DELETE_SELECTOR = 'button[name="delete"]';
	static OBJECT_SELECTOR = '.pb-price-group-list-item:not(.pb-price-group-list-item--perma) [data-id].pb-price-group-list-item__text';

	_form: ObjectForm;
	_objectID: number | null;
	_dia: Dia;
	_priceGroups: IPriceGroup[];

	constructor() {
		this._objectClickEvent = this._objectClickEvent.bind(this);
		this._deleteClickEvent = this._deleteClickEvent.bind(this);
		this._createCTAClickEvent = this._createCTAClickEvent.bind(this);
		this._editFormSubmitEvent = this._editFormSubmitEvent.bind(this);
		this._deleteFormSubmitEvent = this._deleteFormSubmitEvent.bind(this);
		this._form = new ObjectForm();
		this._objectID = null;
		this._priceGroups = [];
		this._dia = new Dia(this._form);
	}

	init(): void {
		setTimeout(async () => (this._priceGroups = await svc.catalog.priceGroup.list()), 22);
		this._form.setId('id_price-group-form');
		let elem: Element | null = document.querySelector(View.CREATE_CTA_SELECTOR);
		if (elem) {
			elem.addEventListener('click', this._createCTAClickEvent);
		}
		document
			.querySelectorAll(View.OBJECT_SELECTOR)
			.forEach(el => el.addEventListener('click', this._objectClickEvent));
		document
			.querySelectorAll(View.DELETE_SELECTOR)
			.forEach(el => el.addEventListener('click', this._deleteClickEvent));
	}

	objectData(): Promise<IPriceGroup> {
		return svc.catalog.priceGroup.get(this.objectID());
	}

	objectID(): number {
		if (isNumber(this._objectID)) {
			return this._objectID;
		}
		throw new Error('View::objectID object ID is null');
	}

	openCreateDialog(): void {
		this._form.setAction(window.pburls.group.catalog.priceGroup.list);
		this._form.setParents(this._priceGroups);
		this._form.nameInput.setValue('');
		this._form.show();
		this._dia.setTitle('Create price group');
		this._dia.setStandardButtons(StandardButton.Cancel | StandardButton.Save);
		const saveBtn = this._dia.button(StandardButton.Save);
		if (saveBtn) {
			saveBtn.setRaised(true);
			saveBtn.setType('submit');
			saveBtn.setAttribute('form', this._form.id());
		}
		this._dia.open();
	}

	openDeleteDialog(data: IPriceGroup): void {
		this._form.hide();
		this._form.onSubmit(this._deleteFormSubmitEvent);
		this._form.setAction();
		this._form.setParents(this._eligibleParents(data));
		this._form.nameInput.setValue(data.name);
		this._dia.setTitle(`Delete ${data.name}?`);
		this._dia.setStandardButtons(StandardButton.No | StandardButton.Yes);
		const noBtn = this._dia.button(StandardButton.No);
		if (noBtn) {
			noBtn.setRaised(true);
		}
		const yesBtn = this._dia.button(StandardButton.Yes);
		if (yesBtn) {
			yesBtn.setType('submit');
			yesBtn.setAttribute('form', this._form.id());
		}
		this._dia.open();
	}

	openDetailDialog(data: IPriceGroup): void {
		this._form.onSubmit(this._editFormSubmitEvent);
		this._form.setAction();
		this._form.setParents(this._eligibleParents(data));
		this._form.nameInput.setValue(data.name);
		this._form.parentSelect.setParentID(data.parentId);
		this._form.show();
		this._dia.setTitle('Edit price group');
		// this._dia.setComponent(this._form);
		this._dia.setStandardButtons(StandardButton.Cancel | StandardButton.Save);
		const saveBtn = this._dia.button(StandardButton.Save);
		if (saveBtn) {
			saveBtn.setRaised(true);
			saveBtn.setType('submit');
			saveBtn.setAttribute('form', this._form.id());
		}
		this._dia.open();
	}

	private _createCTAClickEvent(): void {
		this.openCreateDialog();
	}

	private async _deleteClickEvent(event: Event): Promise<void> {
		event.stopPropagation();
		const tgt = El.fromEvent(event);
		const sib = tgt.nextElementSibling();
		const id = sib ? Number(sib.attribute('data-id')) : Number.NaN;
		if (Number.isNaN(id)) {
			console.log('View::_deleteClickEvent event target data-id attribute did not yield valid value');
			return;
		}
		this._objectID = id;
		const data = await this.objectData();
		this.openDeleteDialog(data);
	}

	private async _deleteFormSubmitEvent(): Promise<void> {
		const data = await this.objectData();
		const objectID = data.id;
		await svc.catalog.priceGroup.delete(objectID);
		this._priceGroups = await svc.catalog.priceGroup.list();
		this._removeDocumentObject(objectID);
		this._dia.accept();
		this._objectID = null;
	}

	private async _editFormSubmitEvent(): Promise<void> {
		let data = await this.objectData();
		const name = this._form.nameInput.value();
		const parentId = this._form.parentSelect.parentID();
		const payload = Object.assign({}, data, {name, parentId});
		data = await svc.catalog.priceGroup.update(payload.id, payload);
		this._priceGroups = await svc.catalog.priceGroup.list();
		this._syncDocumentObject(data);
		this._dia.accept();
		this._objectID = null;
	}

	private _eligibleParents(obj: IPriceGroup): IPriceGroup[] {
		const {treeId, level} = obj;
		return this._priceGroups.filter(obj => {
			if (obj.treeId === treeId) {
				return obj.level < level;
			}
			return true;
		});
	}

	private async _objectClickEvent(event: Event): Promise<void> {
		const tgt = <HTMLElement | null>event.target;
		if (!tgt) {
			console.log('View::_objectClickEvent event target is null');
			return;
		}
		const elem = new El(closestMatchingElement(tgt, View.OBJECT_SELECTOR));
		const id = Number(elem.attribute('data-id'));
		if (Number.isNaN(id)) {
			console.log('View::_objectClickEvent event target data-id attribute did not yield valid value');
			return;
		}
		this._objectID = id;
		const data = await this.objectData();
		this.openDetailDialog(data);
	}

	private _removeDocumentObject(objectID: number): void {
		const docObj = new DocumentObject(objectID);
		docObj.remove();
	}

	private _syncDocumentObject(data: IPriceGroup): void {
		const docObj = new DocumentObject(data.id);
		docObj.setName(data.name);
	}
}

export function init(): void {
	const comp = new View();
	comp.init();
}
